import { useState, useEffect } from 'react';
import {
  Text,
  Group,
  Stack,
  Box,
  Loader,
  Progress,
  Badge,
  Paper,
} from '@mantine/core';
import type {
  AttendanceModality,
  ModalityAttendance,
  ParticipantCohortAttendance,
} from '../../../types';
import { participantsApi } from '../../../services/api/participants';
import { ENGAGEMENT_RATING_MAP } from '../../../config/engagementRatingConfig';
import { AttendanceRate } from '../../../components/common';
import {
  ATTENDANCE_MODALITIES,
  attendedHeadline,
  hasBuckets,
  modalityAttendanceLabel,
} from '../../../utils/attendanceModalityLabels';

/**
 * MED-1442: one attendance card per cohort modality. Without buckets (an
 * older API, or an empty object), the legacy fields make the one group
 * coaching card.
 */
function attendanceBuckets(
  data: ParticipantCohortAttendance
): Array<[AttendanceModality, ModalityAttendance]> {
  if (hasBuckets(data.attendance_by_modality)) {
    return ATTENDANCE_MODALITIES.flatMap(k => {
      const bucket = data.attendance_by_modality?.[k];
      return bucket ? [[k, bucket] as [AttendanceModality, ModalityAttendance]] : [];
    });
  }
  return [
    [
      'group_coaching',
      {
        attended: data.sessions_attended,
        partial: data.sessions_partial,
        absent: data.sessions_absent,
        marked: data.attendance_marked,
        population: data.attendance_population,
        percentage: data.attendance_percentage,
      },
    ],
  ];
}

function AttendanceCard({ title, bucket }: { title: string; bucket: ModalityAttendance }) {
  const { attended, partial, absent, marked, population, percentage } = bucket;
  return (
    <Paper withBorder p="md" radius="sm">
      <Group gap="sm" mb="xs">
        <Text size="sm" fw={600}>{title}</Text>
      </Group>

      {/* MED-1266: the rate + its marking coverage via the shared AttendanceRate component. */}
      <Group justify="space-between" mb={6}>
        <Text size="sm">
          {attendedHeadline(attended + partial, marked, partial)}
        </Text>
        <AttendanceRate
          rate={percentage}
          marked={marked}
          population={population}
          variant="inline"
          size="sm"
          fw={600}
        />
      </Group>

      <Progress.Root size="sm" radius="sm">
        {attended > 0 && (
          <Progress.Section value={(attended / marked) * 100} color="teal" />
        )}
        {partial > 0 && (
          <Progress.Section value={(partial / marked) * 100} color="yellow" />
        )}
        {absent > 0 && (
          <Progress.Section value={(absent / marked) * 100} color="red" />
        )}
      </Progress.Root>

      <Group gap="lg" mt="xs">
        <Group gap={4}>
          <Box w={8} h={8} style={{ borderRadius: '50%', backgroundColor: 'var(--mantine-color-teal-6)' }} />
          <Text size="xs" c="dimmed">Attended ({attended})</Text>
        </Group>
        <Group gap={4}>
          <Box w={8} h={8} style={{ borderRadius: '50%', backgroundColor: 'var(--mantine-color-yellow-6)' }} />
          <Text size="xs" c="dimmed">Partial ({partial})</Text>
        </Group>
        <Group gap={4}>
          <Box w={8} h={8} style={{ borderRadius: '50%', backgroundColor: 'var(--mantine-color-red-6)' }} />
          <Text size="xs" c="dimmed">Absent ({absent})</Text>
        </Group>
      </Group>
    </Paper>
  );
}

interface CohortAttendanceSectionProps {
  cohortId: string;
  participantId: string;
  isActive: boolean;
}

export function CohortAttendanceSection({ cohortId, participantId, isActive }: CohortAttendanceSectionProps) {
  const [data, setData] = useState<ParticipantCohortAttendance | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isActive || !participantId || data) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const result = await participantsApi.getCohortAttendance(cohortId, participantId);
        setData(result);
      } catch {
        // Silently fail - section just won't show data
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [isActive, cohortId, participantId, data]);

  if (loading) {
    return (
      <Box mb="xl">
        <Text
          size="xs"
          fw={600}
          c="dimmed"
          mb="md"
          style={{ letterSpacing: '0.5px', textTransform: 'uppercase' }}
        >
          Cohort Attendance
        </Text>
        <Box ta="center" py="xl">
          <Loader size="sm" />
        </Box>
      </Box>
    );
  }

  // MED-1442: show the section once any modality has a recorded outcome.
  const buckets = data ? attendanceBuckets(data) : [];
  if (!data || !buckets.some(([, b]) => b.marked > 0)) {
    return (
      <Box mb="xl">
        <Text
          size="xs"
          fw={600}
          c="dimmed"
          mb="md"
          style={{ letterSpacing: '0.5px', textTransform: 'uppercase' }}
        >
          Cohort Attendance
        </Text>
        <Text size="sm" c="dimmed">
          No attendance data recorded
        </Text>
      </Box>
    );
  }

  const { engagement_ratings, average_engagement_rating } = data;
  const avgConfig = average_engagement_rating ? ENGAGEMENT_RATING_MAP.get(Math.round(average_engagement_rating)) : null;

  return (
    <Box mb="xl">
      <Text
        size="xs"
        fw={600}
        c="dimmed"
        mb="md"
        style={{ letterSpacing: '0.5px', textTransform: 'uppercase' }}
      >
        Cohort Attendance
      </Text>

      <Stack gap="md">
        {/* Attendance Summary: one card per modality (MED-1442) */}
        {buckets.map(([k, bucket]) => (
          <AttendanceCard
            key={k}
            title={modalityAttendanceLabel(k, data.group_coaching_sub_type)}
            bucket={bucket}
          />
        ))}

        {/* Engagement Ratings */}
        {(engagement_ratings.length > 0 || average_engagement_rating !== null) && (
          <Paper withBorder p="md" radius="sm">
            <Group gap="sm" mb="xs" justify="space-between">
              <Text size="sm" fw={600}>Engagement</Text>
              {average_engagement_rating !== null && avgConfig && (
                <Badge size="sm" color={avgConfig.color} variant="light">
                  Avg: {average_engagement_rating} - {avgConfig.label}
                </Badge>
              )}
            </Group>

            {engagement_ratings.length > 0 ? (
              <Stack gap={6}>
                {engagement_ratings.map((er, idx) => {
                  const ratingConfig = ENGAGEMENT_RATING_MAP.get(Number(er.rating));
                  return (
                    <Group key={idx} gap="sm" py={2}>
                      <Text size="xs" c="dimmed" w={70} style={{ flexShrink: 0 }}>
                        {er.session_number != null ? `Session ${er.session_number}` : er.session_title}
                      </Text>
                      <Badge size="xs" color={ratingConfig?.color || 'gray'} variant="filled" w={20} h={20} p={0} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {er.rating}
                      </Badge>
                      <Text size="xs" c="dimmed" style={{ flex: 1 }}>
                        {ratingConfig?.label || `Rating ${er.rating}`}
                      </Text>
                    </Group>
                  );
                })}
              </Stack>
            ) : (
              <Text size="sm" c="dimmed">No engagement ratings recorded</Text>
            )}
          </Paper>
        )}
      </Stack>
    </Box>
  );
}
